"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  type User,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut as fbSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import { firebaseAuth } from "@/lib/firebase";
import { identify as analyticsIdentify, track } from "@/lib/analytics";
import {
  clearClickIds,
  clearFirstTouch,
  noIntentAttribution,
  clearSignupIntent,
  consumeSignupIntent,
  consumeSignupPending,
  markSignupPending,
  readSignupIntent,
  signupCtaProp,
  trackSignupFailed,
} from "@/lib/signup-funnel";
import { storeDomainMatch } from "@/lib/domain-match";
import { DEV_AUTH_ENABLED, makeDevUser } from "@/lib/dev-auth";
import { isMobileOrTouchBrowser } from "@/lib/device";
import {
  ApiError,
  AuthError,
  bootstrapMe,
  endImpersonation,
  fetchMe,
  sendVerificationEmail,
  clearActiveWorkspacePin,
  captureTimezone,
  setActiveWorkspacePin,
  setImpersonationToken,
  setTenantContextChangedHandler,
  startImpersonation,
  updateTimezone,
  type MeOrg,
  type MeResponse,
  type MeUser,
} from "@/lib/api";
import { logger } from "@/lib/log";
import { unloadIdentityScopedCache } from "@/lib/queries";
import {
  applyTimezoneWrite,
  applyTimezoneWriteWithReconciliation,
  TimezoneWriteGuard,
} from "@/lib/timezone-write-guard";

const log = logger("auth");

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Google sign-in transport: popup on desktop, full-page redirect on mobile.
//
// `signInWithPopup` is unreliable on mobile browsers — mobile Safari/Chrome
// block the popup or drop the opener reference when the account chooser takes
// over the tab, so the flow silently never resolves and sign-in appears to do
// nothing. Firebase's own guidance is to use `signInWithRedirect` there. The
// redirect navigates the whole page to Google and back; both `signIn()` call
// sites (login page, invite accept) persist their pre-sign-in state (signup
// intent, pending invite token) to localStorage *before* calling, so it
// survives the round-trip. `getRedirectResult` completes it on return.
const prefersRedirectSignIn = isMobileOrTouchBrowser;

// Is the Firebase auth handler same-origin with the current page? Redirect only
// completes reliably when it is (see docs/auth-custom-domain.md). `authDomain`
// is a single baked value, but the app is reachable on more than one host — the
// canonical `app.deliverables.ai` and the `app.railyard.ai` alias. (The
// `pilot.deliverables.ai` alias and the raw Netlify URL now redirect to
// canonical per ENG-4823, so sign-in never runs there; this gate still has to
// hold for `app.railyard.ai` and for whatever host comes next.) On a host where
// `authDomain` is cross-origin, `signInWithRedirect`
// fails silently under third-party-storage partitioning (iOS Safari), so we must
// NOT route redirect there — the popup works cross-origin (postMessage) and is
// the right transport for those hosts. (codex P2, ENG-2526)
function authHandlerIsSameOrigin(): boolean {
  if (typeof window === "undefined") return false;
  const authDomain = firebaseAuth.config.authDomain;
  return !!authDomain && authDomain === window.location.host;
}

// Desktop popup failures that mean "this browser can't do popups" (blocked, or
// an embedded/in-app webview that doesn't support them) — fall back to the
// redirect flow rather than dead-ending sign-in. A user-closed popup
// (`auth/popup-closed-by-user`, `auth/cancelled-popup-request`) is deliberate
// and must NOT retry as a redirect.
const POPUP_FALLBACK_CODES = new Set([
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
]);
function isPopupUnsupported(err: unknown): boolean {
  return (
    err instanceof Error &&
    POPUP_FALLBACK_CODES.has((err as { code?: string }).code ?? "")
  );
}

// A user-facing message for a failed redirect sign-in. The redirect leg
// resolves on page load (getRedirectResult), long after the button handler that
// called signIn() is gone — so its errors can't surface at the call site and
// have to be routed through context to the login/invite page. Without this a
// failed redirect leaves the user back on /login with no explanation, i.e. the
// same "nothing happened" the mobile fix is meant to cure (codex P2, ENG-2526).
function friendlyRedirectError(err: unknown): string {
  const code = (err as { code?: string })?.code;
  switch (code) {
    case "auth/account-exists-with-different-credential":
      return "An account already exists with this email using a different sign-in method.";
    case "auth/unauthorized-domain":
      return "This site isn't authorized for Google sign-in yet. Please contact support.";
    default:
      return "Google sign-in didn't complete. Please try again.";
  }
}

// Continue URL appended to verification / password-reset emails. It gives the
// email-action handler a path back into the app: the Firebase-hosted handler
// shows it as a "Continue" link (so the email isn't a dead-end today), and our
// branded /auth/action handler reads it as the post-action redirect target once
// the custom action URL is set in the Firebase console. Only our own
// (Firebase-authorized) origin is ever used. Undefined during SSR.
function appActionCodeSettings() {
  if (typeof window === "undefined") return undefined;
  const { origin, pathname, search } = window.location;
  // Return the user to where they started rather than always the app root:
  // invite routes (/invite/*, /workspace-invite/*) must be revisited for the
  // invite to be redeemed. On /login the user isn't "at" /login — honor the
  // safe ?next= deep-link target AuthGuard may have attached (e.g. a signed-out
  // shared-project link), falling back to root otherwise.
  let path = `${pathname}${search}`;
  if (pathname === "/login") {
    const next = new URLSearchParams(search).get("next");
    path = next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
  }
  return { url: `${origin}${path}` };
}

// Where email verification should return the user after they click "Continue"
// in the branded action handler. Defaults to /new (onboarding); but when signup
// or resend happens on an invite page (or with an invite ?next=), preserve that
// path so the invite is redeemed after verifying rather than dead-ending on /new
// (ENG-1871). Returns a same-origin path; the backend builds the full URL.
function verificationContinuePath(): string {
  if (typeof window === "undefined") return "/new";
  const { pathname, search } = window.location;
  const isInvitePath =
    pathname.startsWith("/invite/") || pathname.startsWith("/workspace-invite/");
  if (isInvitePath) return pathname;
  const next = new URLSearchParams(search).get("next");
  if (next && (next.startsWith("/invite/") || next.startsWith("/workspace-invite/"))) {
    return next;
  }
  return "/new";
}

// Bounded auto-retry for the sign-in bootstrap. A network blip or an API
// cold-start at sign-in shouldn't wedge an authenticated user on a broken
// screen — we retry a few times with exponential backoff before giving up.
const BOOTSTRAP_MAX_ATTEMPTS = 4;
const BOOTSTRAP_BACKOFF_MS = [1000, 2000, 4000];

/**
 * Should a failed bootstrap/fetchMe be retried?
 *
 * Transient (retry): a raw `fetch()` TypeError ("Failed to fetch" — network
 * down, CORS, DNS, or an aborted in-flight request) or a 5xx / status-0
 * `ApiError` (API cold-start, gateway hiccup).
 *
 * Terminal (don't retry): `AuthError` (401 — the token is genuinely rejected;
 * spinning won't help and the user needs to re-auth) and any other `ApiError`
 * (403 permission, 4xx) where the server gave a definite answer.
 */
function isTransientBootstrapError(err: unknown): boolean {
  if (err instanceof AuthError) return false;
  if (err instanceof ApiError) return err.status >= 500 || err.status === 0;
  // fetch() rejects with a TypeError on network failure / CORS / abort.
  return err instanceof TypeError;
}

export type ImpersonationState = {
  targetUid: string;
  targetEmail: string | null;
  targetOrgName: string | null;
  expiresAt: number;
};

type AuthContextValue = {
  user: User | null;
  me: MeUser | null;
  org: MeOrg | null;
  loading: boolean;
  // True when the post-sign-in bootstrap (POST /api/me/bootstrap → GET
  // /api/me) terminally failed after bounded retries, leaving an
  // authenticated Firebase user with no account loaded. AuthGuard renders a
  // recovery card (Retry → `retryBootstrap`) instead of a half-broken shell.
  bootstrapError: boolean;
  retryBootstrap: () => void;
  // True when an email/password user is signed in but hasn't verified their
  // address yet. The app holds them at a verification prompt rather than
  // bootstrapping (the server gates invite acceptance + pending-owner adoption
  // on a verified email; Google sign-in is always verified, so it never trips
  // this). See PR #878 / codex P1.
  needsEmailVerification: boolean;
  resendVerificationEmail: () => Promise<void>;
  refreshVerification: () => Promise<void>;
  isStaff: boolean;
  impersonation: ImpersonationState | null;
  beginImpersonation: (
    targetUid: string,
    hints?: {
      email?: string | null;
      orgName?: string | null;
      /** Pin X-Active-Workspace after impersonation without a full reload
       * (Slack staff deep links — ENG-2951). */
      workspaceTenantId?: string | null;
    },
  ) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  /** Clear an active impersonation session without navigating to /staff.
   * Used by the Slack staff deep-link hop when switching targets mid-session. */
  clearImpersonation: () => Promise<void>;
  // A failed Google *redirect* sign-in (mobile flow) surfaces here, since it
  // resolves on page load after the calling button handler is gone. The login
  // and invite pages render it; clear it when starting a fresh attempt.
  redirectSignInError: string | null;
  clearRedirectSignInError: () => void;
  signIn: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  // ENG-3641: active workspace's firm-website gate answer from GET /api/me.
  // Null/undefined = unanswered. AuthGuard blocks when the onboarding_gate
  // flag is on and this is unset.
  onboardingState: MeOnboardingState | null;
  /**
   * True once `/api/me` has returned a body that *includes* the
   * `onboarding_state` key (even when null). Distinguishes a new API that
   * knows the gate from an older revision that omits the field entirely —
   * without this, a Netlify-ahead-of-Cloud-Run window would treat the
   * missing `onboarding_gate` entry in `disabled_features` as "granted"
   * and block every tenant (codex P1 on #2045).
   */
  onboardingApiReady: boolean;
  /** Optimistic local update after the gate submits (avoids a full reload). */
  markOnboardingComplete: (state: MeOnboardingState) => void;
  /**
   * Re-fetch `/api/me` into context (me / org / onboardingState). Used when
   * Active-Workspace changes without a full reload — e.g. a same-target
   * staff deep-link that only hops `?w=` (codex P2 on #2045).
   */
  refreshMe: (opts?: { tenantRecoveryProbe?: boolean }) => Promise<MeResponse>;
  getIdToken: () => Promise<string | null>;
  setOrg: (org: MeOrg | null) => void;
  setMe: (me: MeUser | null) => void;
  saveTimezone: (timezone: string | null) => Promise<MeResponse>;
};

/** Wire shape of MeResponse.onboarding_state (ENG-3641). Kept local so the
 * gate can compile before openapi regen; matches the OpenAPI schema. */
export type MeOnboardingState = {
  path: "website" | "new_firm";
  website?: string | null;
  completed_at: number;
};

/** Normalize MeResponse.onboarding_state from the API (or undefined on old
 * cached shapes) into the local MeOnboardingState, or null when unanswered. */
function coerceOnboardingState(raw: unknown): MeOnboardingState | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.path !== "website" && o.path !== "new_firm") return null;
  if (typeof o.completed_at !== "number") return null;
  return {
    path: o.path,
    website: typeof o.website === "string" ? o.website : null,
    completed_at: o.completed_at,
  };
}

/** True when the `/api/me` JSON body carries `onboarding_state` (null or
 * object). Key presence — not value — is the API-support signal for the
 * gate; old revisions omit the key entirely. */
function meReportsOnboardingState(res: object): boolean {
  return Object.prototype.hasOwnProperty.call(res, "onboarding_state");
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // In dev-auth mode the user is known up front, so seed it as the initial
  // state rather than setting it inside the effect.
  const [user, setUser] = useState<User | null>(() =>
    DEV_AUTH_ENABLED ? makeDevUser() : null,
  );
  const [me, setMe] = useState<MeUser | null>(null);
  const [org, setOrg] = useState<MeOrg | null>(null);
  const [onboardingState, setOnboardingState] = useState<MeOnboardingState | null>(null);
  const [onboardingApiReady, setOnboardingApiReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(false);
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [redirectSignInError, setRedirectSignInError] = useState<string | null>(null);
  // Monotonic token for the active load. Bumped on every auth change, every
  // manual retry, and on unmount so a stale in-flight retry loop (superseded
  // by a newer auth event, or a provider torn down by a page reload) can
  // neither write state nor keep retrying. The latter is the benign case the
  // bug first surfaced as: a Playwright reload aborts the in-flight fetch
  // mid-bootstrap, which we must not treat as a real failure.
  const loadSeq = useRef(0);
  // The real (pre-impersonation) staff flag. We capture it from the
  // initial /api/me response and stash it separately so the staff console
  // remains reachable even while `me` reflects an impersonated non-staff
  // user.
  const [isStaff, setIsStaff] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);
  // Per-uid latch so bootstrap runs at most once per signed-in user.
  const bootstrappedFor = useRef<string | null>(null);
  // Per-uid latch (ENG-3796) so the one-shot timezone capture below fires at
  // most once per signed-in user per session — a retried/re-triggered
  // loadAccount for the same uid must not re-PATCH.
  const timezoneCapturedFor = useRef<string | null>(null);
  const timezoneWriteGuard = useRef(new TimezoneWriteGuard());
  const pendingTimezoneCapture = useRef<{
    uid: string;
    promise: Promise<MeResponse>;
  } | null>(null);
  // Coalesces concurrent verification re-checks. The recheck effect listens on
  // both `focus` and `visibilitychange`, which fire back-to-back when the user
  // returns to the tab after clicking the emailed link — without this they'd
  // each kick off a reload + token-refresh + loadAccount chain.
  const refreshingVerification = useRef(false);

  function readPendingInviteToken(): string | null {
    try {
      return window.localStorage.getItem("railyard_pending_invite_token");
    } catch {
      return null;
    }
  }
  function clearPendingInviteToken() {
    try {
      window.localStorage.removeItem("railyard_pending_invite_token");
    } catch {
      /* ignore */
    }
  }

  // Bootstrap once per uid. POST /api/me/bootstrap unconditionally — the
  // server returns the existing user record if it exists, or creates a
  // fresh Org + User otherwise. Idempotent, so the same call works for
  // both first-time sign-in and returning users.
  //
  // Invite handling hooks in here: the accept page stores the invite token in
  // localStorage before triggering sign-in, and we pass it to bootstrap. Since
  // ENG-1874 the token does NOT make bootstrap join the inviting org — every
  // user gets their own personal home org. The token only marks the signup
  // invite-driven so the server suppresses the one-time trial grant (joining a
  // team isn't a solo trial). The actual membership is added by the accept
  // page's explicit acceptWorkspaceInvite call. The token is consumed (cleared)
  // on a terminal response (success or a definite server error), but kept across
  // transient retries.
  //
  // A verify-gate 403 seen here is a stale-token race, NOT a genuinely-
  // unverified user: we only reach ensureBootstrapped for a verified user
  // (onAuthStateChanged holds unverified ones at the notice), so the address IS
  // verified server-side. It surfaces right after the email link is clicked on
  // Firebase's action page (ballcap.firebaseapp.com) — a different origin than
  // the app, so this origin's ID token was never refreshed and still carries
  // email_verified=false. The server raises it on provisioned-pending-owner
  // adoption (the email-not-verified branch). Force a hard token refresh and
  // retry rather than treating it as terminal. Separately, never discard the
  // invite token for a verify race, else an invite retry would lose the
  // trial-suppression signal. (ENG-1871; the cross-origin hop goes away once
  // the email action URL is same-origin — ENG-1868 — but keep this as defense
  // in depth.)
  const VERIFY_RACE_RETRIES = 2;
  const isVerifyRace = (err: unknown) =>
    err instanceof ApiError &&
    !(err instanceof AuthError) &&
    err.status === 403 &&
    /verify your email/i.test(err.detail);

  async function ensureBootstrapped(
    u: User,
    signupAttribution: Record<string, string> | null,
  ) {
    if (bootstrappedFor.current === u.uid) return;
    const inviteToken = readPendingInviteToken();
    for (let verifyRetry = 0; ; verifyRetry++) {
      try {
        const res = await bootstrapMe({
          display_name: u.displayName ?? undefined,
          invite_token: inviteToken ?? undefined,
          signup_attribution: signupAttribution,
        });
        // Server-authoritative completion signal: bootstrap `created` is the only
        // thing that correctly means "a new signup conversion" — it covers a fresh
        // org AND a seeded-trial adoption, and excludes invite acceptances,
        // grandfathered/migrated adoptions, and returning users (all of which also
        // create a Firebase account, so a Firebase-side isNewUser signal would
        // overcount them). The marker is uid-keyed + persisted so it survives a
        // reload (stale-workspace 401) between here and the event.
        // Known limitation (accepted): if this created=true response is lost to a
        // transient error, the retry returns created=false and signup_completed is
        // undercounted for that one signup. A rare under-count is preferable to
        // over-counting every invite/migration join, so we don't key off Firebase.
        if (res.created) markSignupPending(u.uid, Boolean(res.trial_granted));
        // Domain-match banner (ENG-2509, Phase 0b): bootstrap returns the
        // match exactly once (fresh self-serve signup, verified email whose
        // domain an existing workspace claimed). Stash it per-uid so the app
        // shell's DomainMatchBanner can render it — including across the
        // reload that can happen between here and first paint.
        if (res.domain_match) storeDomainMatch(u.uid, res.domain_match);
        break;
      } catch (err) {
        // Defensive: bootstrap no longer 409s on an invite token (an existing
        // user just gets their record back since ENG-1874), but if the server
        // ever reports the account already exists, treat it as
        // already-bootstrapped — consume the token, latch the uid, and let
        // fetchMe load the existing account rather than surfacing the recovery
        // card. (Also covers a returning user with a stale token in localStorage.)
        if (err instanceof ApiError && err.status === 409) {
          if (inviteToken) clearPendingInviteToken();
          bootstrappedFor.current = u.uid;
          return;
        }
        // Stale-token verify race (see note above): refresh the ID token and
        // retry. Runs for any verify-gate 403 (invite acceptance OR pending-
        // owner adoption), not only invite bootstraps. Bounded so a genuinely
        // stuck claim can't loop forever.
        if (isVerifyRace(err) && verifyRetry < VERIFY_RACE_RETRIES) {
          try {
            await u.getIdToken(true);
          } catch {
            /* best-effort; a persistent failure falls through to terminal */
          }
          await sleep(BOOTSTRAP_BACKOFF_MS[verifyRetry] ?? 1000);
          continue;
        }
        // Consume the token on any other terminal outcome (a definite server
        // answer) — the user can re-visit the invite URL to retry. But KEEP it
        // for a transient failure so the auto-retry still carries the invite,
        // otherwise a network blip would silently drop the user into a fresh org
        // instead of the inviting one. A verify-race 403 that outlived the
        // refresh retries above is also kept (never the invite token's fault),
        // so the recovery-card Retry can still join the inviting workspace.
        if (inviteToken && !isTransientBootstrapError(err) && !isVerifyRace(err)) {
          clearPendingInviteToken();
        }
        throw err;
      }
    }
    // Bootstrap succeeded — consume the token and latch the uid so we don't
    // re-bootstrap on a fetchMe retry.
    if (inviteToken) clearPendingInviteToken();
    bootstrappedFor.current = u.uid;
  }

  // One-shot timezone capture (ENG-3796): if the just-loaded user has no
  // `timezone` set, save the browser's own resolved IANA zone if it is still unset.
  // Fire-and-forget — a failure (offline, invalid zone in some exotic
  // environment) must never block or retry the account load; the next load
  // (or the account settings field) gets another chance. Latched per-uid so
  // a re-render or a retried loadAccount for the same signed-in user doesn't
  // re-PATCH every time.
  function captureTimezoneOnce(user: MeUser | null) {
    if (!user || user.timezone) return;
    if (timezoneCapturedFor.current === user.uid) return;
    timezoneCapturedFor.current = user.uid;
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      return; // can't resolve a zone in this environment — leave it unset
    }
    if (!tz) return;
    const capturedForUid = user.uid;
    const ticket = timezoneWriteGuard.current.begin(capturedForUid);
    const captureRequest = captureTimezone(tz);
    const pending = { uid: capturedForUid, promise: captureRequest };
    pendingTimezoneCapture.current = pending;
    void applyTimezoneWrite(
      timezoneWriteGuard.current,
      ticket,
      () => captureRequest,
      setMe,
    )
      .catch((err) => {
        log.warn("timezone capture failed", err);
      })
      .finally(() => {
        if (pendingTimezoneCapture.current === pending) {
          pendingTimezoneCapture.current = null;
        }
      });
  }

  async function saveTimezone(timezone: string | null): Promise<MeResponse> {
    const uid = me?.uid;
    if (!uid) throw new Error("Cannot save timezone without a signed-in user");
    const ticket = timezoneWriteGuard.current.begin(uid);
    const pendingCapture =
      pendingTimezoneCapture.current?.uid === uid
        ? pendingTimezoneCapture.current.promise
        : null;
    return applyTimezoneWriteWithReconciliation(
      timezoneWriteGuard.current,
      ticket,
      () => updateTimezone(timezone),
      fetchMe,
      pendingCapture,
      setMe,
    );
  }

  // Drive POST /api/me/bootstrap → GET /api/me with bounded retry/backoff.
  // Keeps `loading` true across retries (AuthGuard shows its spinner) and only
  // settles on a terminal outcome: success (me/org populated) or
  // `bootstrapError` (recovery card). Guarded by `loadSeq` so a superseded or
  // unmounted load is a no-op — see the ref comment above.
  async function loadAccount(u: User) {
    const seq = ++loadSeq.current;
    const isCurrent = () => loadSeq.current === seq;
    // Read once for this load attempt sequence. The valid intent remains in
    // storage until signup completion, while every verification/network retry
    // sends this same frozen first-touch payload.
    // With no intent (a Google sign-in from the default sign-in mode that
    // created the account) still send the first touch, landing campaign fields
    // and any stashed click id; the server adds `src`.
    const signupAttribution = readSignupIntent()?.attribution ?? noIntentAttribution();
    setBootstrapError(false);
    // Show the spinner while we (re)load. Matters when signing in after a
    // signed-out state (loading is already false): without this, a transient
    // bootstrap failure would land the user in AppShell with me/org unset for
    // the whole retry window instead of the spinner/recovery flow. No-op on
    // first page load, where loading already starts true.
    setLoading(true);
    for (let attempt = 1; ; attempt++) {
      try {
        await ensureBootstrapped(u, signupAttribution);
        const res = await fetchMe();
        if (!isCurrent()) return;
        timezoneWriteGuard.current.transition(res.user?.uid ?? null);
        setMe(res.user);
        setOrg(res.org);
        setOnboardingState(coerceOnboardingState(res.onboarding_state));
        setOnboardingApiReady(meReportsOnboardingState(res));
        setIsStaff(Boolean(res.user?.is_staff));
        captureTimezoneOnce(res.user);
        // Late-verifier domain-match banner (ENG-2509, Phase 0b): GET /api/me
        // recomputes the match on every load, catching an email/password
        // signup who bootstrapped unverified. storeDomainMatch is
        // tombstone-aware, so a user who already chose "Start my own
        // workspace" is never re-nagged by this re-fetch.
        if (res.domain_match && res.user?.uid) {
          storeDomainMatch(res.user.uid, res.domain_match);
        }
        analyticsIdentify({
          userId: res.user?.uid ?? u.uid,
          email: res.user?.email ?? u.email,
          displayName: res.user?.display_name ?? u.displayName,
          tenantId: res.org?.id ?? null,
          tenantName: res.org?.name ?? null,
          workspaceKind: res.org?.workspace_kind ?? null,
          isStaff: Boolean(res.user?.is_staff),
        });
        // Funnel terminal event: fire once for a genuine new signup, AFTER
        // identify so it attributes to the new user. The pending marker is set on
        // bootstrap `created` and consumed here (survives a reload between the
        // two). `funded` distinguishes a real $100 trial from the walled-at-$0
        // state — `plan` alone can't, since both read "trial". Source/method come
        // from the intent stashed at signup_started (may be absent if storage was
        // cleared across the verify hop — the event still fires, unattributed).
        const pending = consumeSignupPending(u.uid);
        if (pending) {
          const intent = consumeSignupIntent();
          track("signup_completed", {
            plan: res.org?.plan ?? null,
            funded: pending.funded,
            source: intent?.source ?? null,
            method: intent?.method ?? null,
            ...signupCtaProp(intent),
          });
        }
        setBootstrapError(false);
        setLoading(false);
        return;
      } catch (err) {
        // Superseded by a newer auth event, or the provider unmounted (e.g. a
        // page reload aborted the fetch). Benign — don't surface or retry.
        if (!isCurrent()) return;
        if (isTransientBootstrapError(err) && attempt < BOOTSTRAP_MAX_ATTEMPTS) {
          log.warn(`auth bootstrap attempt ${attempt} failed; retrying`, err);
          await sleep(BOOTSTRAP_BACKOFF_MS[attempt - 1] ?? 4000);
          if (!isCurrent()) return;
          continue;
        }
        // Terminal: retries exhausted, or a non-transient (401/403/4xx) error.
        // Keep the Firebase user signed in but surface a recovery affordance
        // rather than a silent console error + half-loaded shell.
        log.error("auth bootstrap failed", err);
        timezoneWriteGuard.current.transition(null);
        setMe(null);
        setOrg(null);
        setOnboardingState(null);
        setOnboardingApiReady(false);
        setBootstrapError(true);
        setLoading(false);
        return;
      }
    }
  }

  // Manual retry from the AuthGuard recovery card. Re-runs the load for the
  // currently signed-in Firebase user; loadAccount flips the spinner on.
  function retryBootstrap() {
    const u = firebaseAuth.currentUser;
    if (!u) return;
    void loadAccount(u);
  }

  useEffect(() => {
    const timezoneGuard = timezoneWriteGuard.current;
    // Hermetic dev/test mode: skip Firebase entirely, bootstrap the
    // synthesized user against the backend's header-auth path. See
    // lib/dev-auth.ts.
    if (DEV_AUTH_ENABLED) {
      // Header-auth users resolve to the synthetic "anonymous" identity on the
      // server — there's no Firebase uid to attach to an org, so skip the
      // /api/me/bootstrap call (which hard-401s without a bearer token) and
      // read /api/me directly.
      void (async () => {
        try {
          const res = await fetchMe();
          timezoneWriteGuard.current.transition(res.user?.uid ?? null);
          setMe(res.user);
          setOrg(res.org);
          setOnboardingState(coerceOnboardingState(res.onboarding_state));
          setOnboardingApiReady(meReportsOnboardingState(res));
          setIsStaff(Boolean(res.user?.is_staff));
          captureTimezoneOnce(res.user);
          analyticsIdentify({
            userId: res.user?.uid ?? null,
            email: res.user?.email ?? null,
            displayName: res.user?.display_name ?? null,
            tenantId: res.org?.id ?? null,
            tenantName: res.org?.name ?? null,
            workspaceKind: res.org?.workspace_kind ?? null,
            isStaff: Boolean(res.user?.is_staff),
          });
        } catch (err) {
          log.error("dev-auth fetchMe failed", err);
          timezoneWriteGuard.current.transition(null);
          setMe(null);
          setOrg(null);
          setOnboardingState(null);
          setOnboardingApiReady(false);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }
    const signedOut = () => {
      // Supersede any in-flight load so a late retry can't repopulate the
      // account after sign-out.
      loadSeq.current++;
      timezoneGuard.transition(null);
      setMe(null);
      setOrg(null);
      setOnboardingState(null);
      setOnboardingApiReady(false);
      setIsStaff(false);
      setImpersonation(null);
      setImpersonationToken(null);
      bootstrappedFor.current = null;
      analyticsIdentify({ userId: null });
      setBootstrapError(false);
      setNeedsEmailVerification(false);
      setLoading(false);
    };
    // Complete a pending mobile/redirect Google sign-in. The happy path is
    // driven by onAuthStateChanged below (which fires with the returning user),
    // but calling getRedirectResult is what surfaces errors from the redirect
    // leg — they can't be caught at the signIn() call site, since that page
    // navigated away.
    //
    // It also gates the first signed-out render: after the redirect round-trip
    // lands back on the app, onAuthStateChanged reports null BEFORE the
    // redirect result finishes processing (several seconds on a slow mobile
    // network), and treating that early null as "signed out" flashes the login
    // form mid-sign-in (reported 2026-07-03, mobile). Defer the signed-out
    // transition until getRedirectResult settles — with no redirect pending it
    // settles near-instantly, so normal signed-out visits are unaffected.
    let redirectSettled = false;
    let deferredSignedOut = false;
    const settleRedirect = () => {
      if (redirectSettled) return;
      redirectSettled = true;
      clearTimeout(redirectTimer);
      if (deferredSignedOut && !firebaseAuth.currentUser) signedOut();
    };
    // Bound the gate so a wedged redirect check (flaky network mid-check)
    // degrades to the old flash-then-form behavior, never an infinite spinner.
    // (settleRedirect only runs async, so referencing the const here is safe.)
    const redirectTimer = setTimeout(settleRedirect, 12_000);
    void getRedirectResult(firebaseAuth)
      .then(() => settleRedirect())
      .catch((err) => {
        log.error("google redirect sign-in failed", err);
        // The signup attempt that left this page via redirect fails here, not
        // at the login page's catch; the stashed intent says it was a signup.
        if (readSignupIntent()?.method === "google") trackSignupFailed("google", err);
        setRedirectSignInError(friendlyRedirectError(err));
        settleRedirect();
      });
    const unsub = onAuthStateChanged(firebaseAuth, (u) => {
      const nextUid = u?.uid ?? null;
      timezoneWriteGuard.current.transition(nextUid);
      setUser(u);
      if (!u) {
        if (!redirectSettled) {
          // Keep `loading` true (boot spinner) instead of rendering signed-out
          // UI; settleRedirect replays this transition if no user materializes.
          deferredSignedOut = true;
          return;
        }
        signedOut();
        return;
      }
      // Email/password sign-ups arrive here unverified. Don't bootstrap them:
      // the server's verified-email gate would 403 invite acceptance and
      // pending-owner adoption, and minting a home org for an unverified
      // address is exactly the leaked-link risk that gate closes. Hold at the
      // verification prompt until the address is confirmed (Google sign-in is
      // always verified and skips this). See PR #878 / codex P1.
      if (!u.emailVerified) {
        // The cached user may be stale — the address was just confirmed via the
        // email link, possibly on a sibling origin (e.g. app.deliverables.ai)
        // whose Firebase storage this origin can't see, so a fresh navigation
        // here lands with emailVerified=false and no focus/visibility event to
        // trigger the recheck. Reload once before holding them at the notice;
        // verification is server-side, so the reload picks it up. (codex #970)
        loadSeq.current++;
        timezoneWriteGuard.current.transition(null);
        const seq = loadSeq.current;
        setMe(null);
        setOrg(null);
        setOnboardingState(null);
        setOnboardingApiReady(false);
        setBootstrapError(false);
        setLoading(true);
        void (async () => {
          try {
            await u.reload();
          } catch {
            /* offline / rate-limited — fall through to the notice */
          }
          if (loadSeq.current !== seq) return;
          const fresh = firebaseAuth.currentUser;
          if (fresh?.emailVerified) {
            try {
              await fresh.getIdToken(true);
            } catch {
              /* best-effort token refresh */
            }
            setUser(fresh);
            setNeedsEmailVerification(false);
            void loadAccount(fresh);
          } else {
            setNeedsEmailVerification(true);
            setLoading(false);
          }
        })();
        return;
      }
      setNeedsEmailVerification(false);
      void loadAccount(u);
    });
    // Unmount (e.g. a page reload) supersedes any in-flight retry loop so it
    // can't setState on a torn-down provider or keep spinning. Bumping the
    // live ref counter is the intent here (not reading a stale captured node),
    // so the ref-in-cleanup lint hint doesn't apply.
    return () => {
      loadSeq.current++; // eslint-disable-line react-hooks/exhaustive-deps
      timezoneGuard.invalidate();
      clearTimeout(redirectTimer);
      unsub();
    };
    // Run once: this subscribes to onAuthStateChanged for the provider's
    // lifetime. loadAccount/ensureBootstrapped close only over stable refs and
    // state setters, so they don't belong in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signIn() {
    // Clear any stale redirect-leg error from a previous failed attempt.
    setRedirectSignInError(null);
    const provider = new GoogleAuthProvider();
    // Mobile: prefer the redirect flow (popups are unreliable) — but ONLY when
    // the auth handler is same-origin, since a cross-origin redirect fails
    // silently on iOS Safari. On a cross-origin host, fall through to the popup
    // (which works cross-origin) rather than dead-end the user with no feedback.
    if (prefersRedirectSignIn() && authHandlerIsSameOrigin()) {
      await signInWithRedirect(firebaseAuth, provider);
      return; // navigates away; getRedirectResult resolves it on return
    }
    // Desktop (and cross-origin mobile): prefer the popup. Fall back to redirect
    // when the browser blocks/can't open popups — but again only if the handler
    // is same-origin, else the redirect would just silently fail too; there we
    // let the popup error surface instead.
    try {
      await signInWithPopup(firebaseAuth, provider);
    } catch (err) {
      if (isPopupUnsupported(err) && authHandlerIsSameOrigin()) {
        await signInWithRedirect(firebaseAuth, provider);
        return;
      }
      throw err;
    }
  }

  async function signInWithEmail(email: string, password: string) {
    await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
  }

  async function signUpWithEmail(email: string, password: string, displayName?: string) {
    const cred = await createUserWithEmailAndPassword(firebaseAuth, email.trim(), password);
    if (displayName?.trim()) {
      // Best-effort: surfaces the name in the profile menu. The org/user
      // record is created by ensureBootstrapped below; the onAuthStateChanged
      // handler will see u.displayName once it fires.
      try {
        await updateProfile(cred.user, { displayName: displayName.trim() });
      } catch {
        /* ignore — displayName is a polish item, not a correctness one */
      }
    }
    // Kick off email verification via the backend. The backend generates a
    // Firebase oobCode via the Admin SDK, rebuilds the link to our branded
    // /auth/action handler on the app host (app.deliverables.ai — NOT the
    // Firebase authDomain), and sends via SendGrid — so the user receives
    // Deliverables AI branding instead of Firebase's bare
    // "ballcap.firebaseapp.com" page. Best-effort: a failure is recoverable
    // via the "Resend" affordance on the verification notice.
    try {
      await sendVerificationEmail(verificationContinuePath());
    } catch (err) {
      log.warn("sendVerificationEmail (backend) failed", err);
    }
  }

  async function resetPassword(email: string) {
    await sendPasswordResetEmail(firebaseAuth, email.trim(), appActionCodeSettings());
  }

  async function resendVerificationEmail() {
    // Guard on a live Firebase session: the backend authenticates the resend
    // with the current user's bearer token, so a signed-out call would 401.
    // Mirrors the old client-SDK guard (`if (!currentUser) return`).
    if (!firebaseAuth.currentUser) return;
    // Route through the backend so the resend also uses our branded /auth/action
    // handler on the app host and SendGrid, not Firebase's default handler.
    await sendVerificationEmail(verificationContinuePath());
  }

  // Re-check the verification status (the user just clicked the emailed link).
  // On success, force a fresh ID token carrying email_verified=true — the
  // cached token still says false, and a verified-gated 403 (unlike a 401)
  // won't trigger apiFetch's force-refresh — then resume the normal bootstrap.
  async function refreshVerification() {
    const u = firebaseAuth.currentUser;
    if (!u) return;
    if (refreshingVerification.current) return;
    refreshingVerification.current = true;
    try {
      await u.reload();
      const fresh = firebaseAuth.currentUser;
      if (fresh?.emailVerified) {
        try {
          await fresh.getIdToken(true);
        } catch {
          /* best-effort token refresh */
        }
        // Capture ?next= before state changes can update the URL. If the user
        // hit "I've verified" while on /login?next=/task/abc, honor it after
        // bootstrap instead of defaulting to /new (ENG-1952).
        const nextPath =
          typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("next")
            : null;
        setUser(fresh);
        setNeedsEmailVerification(false);
        await loadAccount(fresh);
        if (nextPath && nextPath.startsWith("/") && !nextPath.startsWith("//")) {
          window.location.assign(nextPath);
        }
      }
    } finally {
      refreshingVerification.current = false;
    }
  }

  async function signOut() {
    // Drop any leftover signup intent so it can't attribute the next user's
    // signup_completed in this browser to a stale source/method, and the
    // first-touch click ids so the next signup here can't inherit this
    // user's ad click.
    clearSignupIntent();
    clearClickIds();
    clearFirstTouch();
    await fbSignOut(firebaseAuth);
  }

  async function beginImpersonation(
    targetUid: string,
    hints?: {
      email?: string | null;
      orgName?: string | null;
      workspaceTenantId?: string | null;
    },
  ) {
    // Drop any leftover Active-Workspace *before* minting the token.
    // `/api/staff/impersonate` is membership-gated on the real staff identity
    // and is not workspace-exempt, so a stale customer workspace from a prior
    // hop would 403 the mint (ENG-2951 / Codex). Clear directly rather than
    // via setActiveWorkspace, which reloads the page and would drop the
    // in-memory impersonation token we are about to set. Clears BOTH stores:
    // the session-scoped pin (a prior hop's customer workspace) and the staff
    // user's own durable one. Keeping the latter would mean the mint carries
    // their X-Active-Workspace, which 403s if that membership has since gone --
    // not worth the convenience of restoring it afterwards (ENG-4310).
    clearActiveWorkspacePin();
    const res = await startImpersonation(targetUid);
    setImpersonationToken(res.impersonation_token);
    timezoneWriteGuard.current.transition(targetUid);
    // Impersonation switches the effective identity to the target. Start in
    // the target's home org (no Active-Workspace); the workspace switcher (or
    // a staff deep-link's workspaceTenantId) can move to a workspace the
    // target actually belongs to.
    // Refresh /api/me so the rest of the UI re-renders as the target user.
    // The token is already in apiFetch's header set, so fetchMe returns
    // the target's identity.
    let meRes = await fetchMe();
    // Optional workspace pin (ENG-2951 Slack staff hop): after the home-org
    // /api/me succeeds, switch Active-Workspace and re-fetch so chrome + API
    // agree on the task's tenant — without a full reload that would drop the
    // in-memory impersonation token.
    if (hints?.workspaceTenantId && typeof window !== "undefined") {
      // Stamped with the session id minted just above, so this pin dies with
      // the token instead of outliving it (ENG-4155).
      setActiveWorkspacePin(hints.workspaceTenantId);
      try {
        meRes = await fetchMe();
      } catch {
        // Target isn't a member (or access was revoked) — fall back to home org
        // rather than leaving auth half-broken. getTask will then 404 clearly.
        clearActiveWorkspacePin();
      }
    }
    timezoneWriteGuard.current.transition(meRes.user?.uid ?? targetUid);
    setMe(meRes.user);
    setOrg(meRes.org);
    setOnboardingState(coerceOnboardingState(meRes.onboarding_state));
    setOnboardingApiReady(meReportsOnboardingState(meRes));
    // Re-identify so events fired while impersonating attribute to the TARGET's
    // user/tenant, not the staff user's previous Amplitude identity.
    analyticsIdentify({
      userId: meRes.user?.uid ?? targetUid,
      email: meRes.user?.email ?? hints?.email ?? null,
      displayName: meRes.user?.display_name ?? null,
      tenantId: meRes.org?.id ?? null,
      tenantName: meRes.org?.name ?? hints?.orgName ?? null,
      workspaceKind: meRes.org?.workspace_kind ?? null,
      isStaff: Boolean(meRes.user?.is_staff),
    });
    setImpersonation({
      targetUid: res.target.uid,
      targetEmail: res.target.email ?? hints?.email ?? null,
      targetOrgName: hints?.orgName ?? meRes.org?.name ?? null,
      expiresAt: res.expires_at,
    });
  }

  async function clearImpersonation() {
    // Soft end: audit-log the close and drop the in-memory token, but do NOT
    // navigate to /staff. The Slack staff deep-link hop uses this when it
    // needs to switch from one impersonation target to another without losing
    // the /task/<id>?as=… URL.
    try {
      await endImpersonation();
    } finally {
      timezoneWriteGuard.current.transition(firebaseAuth.currentUser?.uid ?? null);
      setImpersonationToken(null);
      setImpersonation(null);
      // Drop the previous target's workspace pin so the next mint (or a plain
      // staff request) doesn't send a customer X-Active-Workspace the real
      // staff user isn't a member of.
      clearActiveWorkspacePin();
    }
  }

  /** Pull a fresh `/api/me` into React state after an Active-Workspace pin
   * changes without a page reload (staff same-target `?w=` hops). */
  async function refreshMe(opts?: { tenantRecoveryProbe?: boolean }) {
    const res = await fetchMe(opts);
    timezoneWriteGuard.current.transition(res.user?.uid ?? null);
    setMe(res.user);
    setOrg(res.org);
    setOnboardingState(coerceOnboardingState(res.onboarding_state));
    setOnboardingApiReady(meReportsOnboardingState(res));
    analyticsIdentify({
      userId: res.user?.uid ?? null,
      email: res.user?.email ?? null,
      displayName: res.user?.display_name ?? null,
      tenantId: res.org?.id ?? null,
      tenantName: res.org?.name ?? null,
      workspaceKind: res.org?.workspace_kind ?? null,
      isStaff: Boolean(res.user?.is_staff),
    });
    return res;
  }

  // The transport drops a stale workspace pin mid-session on an impersonated
  // WORKSPACE_ACCESS_DENIED, and deliberately does not reload (that would end
  // the impersonation session). Nothing else would then re-resolve the tenant,
  // so `org` and the tenant-scoped caches would keep describing a workspace the
  // wire has already left — and a later click could write to a tenant the
  // screen never showed. Re-resolve here instead: the transport announces, this
  // provider owns the recovery (ENG-4316).
  useEffect(() => {
    setTenantContextChangedHandler(async () => {
      try {
        // Marked as the resolution's own read, so a denial on it retries
        // rather than raising a resolution that would wait on itself.
        await refreshMe({ tenantRecoveryProbe: true });
      } catch (e) {
        // Do NOT swallow this. The pin is already gone, so completing the
        // recovery "successfully" would release gated mutations against the
        // home tenant while `org` still describes the previous workspace
        // (codex P1). Rethrow so they stay blocked until the tenant is known —
        // but unload the caches first, since they hold the old tenant's data
        // either way.
        log.warn("tenant re-resolve after a dropped workspace pin failed", e);
        unloadIdentityScopedCache();
        throw e;
      }
      // `unload()`, not a per-key invalidate and not a blanket
      // `mutate(() => true, …)`. queries.ts spells out why: a matcher only
      // matches keys that already hold a cache entry, so a response still in
      // flight for the PREVIOUS tenant is invisible to it and lands afterwards,
      // repopulating the cache under the new one. This is the identity-switch
      // boundary that helper exists for (codex P1).
      unloadIdentityScopedCache();
    });
    return () => setTenantContextChangedHandler(null);
  }, []);

  async function stopImpersonation() {
    try {
      await clearImpersonation();
    } finally {
      // Reset analytics so nothing fires under the target's identity after the
      // swap-back; the /staff reload re-bootstraps and re-identifies as staff.
      analyticsIdentify({ userId: null });
      // Reload the page rather than refetching state piecewise — clears
      // any per-user caches (SWR keys, in-flight task subscriptions, etc.)
      // without needing to know what every panel holds onto.
      if (typeof window !== "undefined") {
        window.location.assign("/staff");
      }
    }
  }

  async function getIdToken(): Promise<string | null> {
    return user ? user.getIdToken() : null;
  }

  // While awaiting email verification, re-check when the tab regains focus —
  // the user clicks the verification link (often in another tab or their mail
  // app) and returns here, at which point we proceed automatically.
  useEffect(() => {
    if (!needsEmailVerification) return;
    const recheck = () => {
      // Only recheck when the tab is actually visible. A visibilitychange fires
      // on hide too (the user leaving to click the email link) — rechecking
      // then would start a reload that's still in flight when they return,
      // letting the in-flight guard swallow the real post-return check and
      // stall auto-continue. (codex #970)
      if (document.visibilityState !== "visible") return;
      void refreshVerification();
    };
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
    // refreshVerification is recreated each render but closes only over stable
    // refs/setters; re-subscribing solely on the flag is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsEmailVerification]);

  return (
    <AuthContext.Provider
      value={{
        user,
        me,
        org,
        loading,
        bootstrapError,
        retryBootstrap,
        needsEmailVerification,
        resendVerificationEmail,
        refreshVerification,
        redirectSignInError,
        clearRedirectSignInError: () => setRedirectSignInError(null),
        isStaff,
        impersonation,
        beginImpersonation,
        clearImpersonation,
        stopImpersonation,
        signIn,
        signInWithEmail,
        signUpWithEmail,
        resetPassword,
        signOut,
        onboardingState,
        onboardingApiReady,
        markOnboardingComplete: (state: MeOnboardingState) => setOnboardingState(state),
        refreshMe,
        getIdToken,
        setOrg,
        setMe,
        saveTimezone,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
